import { Component, ElementRef, EventEmitter, Input, OnInit, Output, Renderer2, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, skip, tap } from 'rxjs/operators';
import { CriteriaService } from 'src/app/services/index.service';
import { ISingleLikertQuestion } from 'src/app/shared/interfaces/single-likert-question';
import { hatchFade } from '../hatch-skin/hatch-animation';

interface IData{
  isUnknown:boolean;
  comment:string|null;
}
interface IAnswer{
  id:number;
  answer:number[];
  valid:boolean;
  isUnknown:boolean;
}

@Component({
  selector: 'app-likert',
  templateUrl: './likert.component.html',
  styleUrls: ['./likert.component.scss'],
  animations: [hatchFade]
})
export class LikertComponent implements OnInit {
  @Input()data:ISingleLikertQuestion = <ISingleLikertQuestion>{};
  @Input()questionNumber:number = -1;
  @Input()config:any;
  @Input()nextPage$:Observable<boolean> = new Observable<boolean>();
  @Input()answers:IAnswer[] = []
  @Output()dataEmitter:EventEmitter<IAnswer> = new EventEmitter<IAnswer>();
  @Output()scrollToNextQuestion:EventEmitter<number> = new EventEmitter<number>();
  checkFormGroup:boolean = false;
  commentStatus:BehaviorSubject<boolean> = new BehaviorSubject(false)
  commentStatusObs$:Observable<boolean> = this.commentStatus.asObservable()
  outputData:IAnswer = {
    id: 0,
    answer: [-1],
    valid: false,
    isUnknown: false,
  }
  extraData:IData = <IData>{};
  customW:string[] = []
  width:{
    lowCount:number[];
    highCount:number[];
    activePercent:number[];
    activePx:number[];
  } = {
    lowCount : [25, 18, 14, 18, 25],
    highCount: [14, 12, 10.5, 8.5, 8, 8.5, 10.5, 12, 14],
    activePercent: [0],
    activePx: [],
  }
  isUnknownInital:boolean|null = null;
  @ViewChild('optionListParent')optionListParent:any;
  constructor(
    private criteriaService: CriteriaService,
    private renderer:Renderer2,
    private toastr:ToastrService,
  ) {}
    
  ngOnInit(): void {
    this.getInitialData()
    this.customW = this.setWidth(this.data.options.length)
    this.nextPageAction()
    // console.log('custom width', this.customW)
  }
  ngAfterViewInit(): void {
    this.setActiveWidth()
  }
  setActiveWidth(){
    let offsetWidth$ = new Observable<number>(observer => {
      const resizeObserver = new ResizeObserver(entries => {
        for (let entry of entries) {
          // Emit the offset width of the element
          observer.next(entry.contentRect.width);
        }
      });
  
      // Start observing the element
      resizeObserver.observe(this.optionListParent.nativeElement);
    });
    offsetWidth$.pipe(
      tap(i=>this.updateActivePx(i))
    ).subscribe()
  }
  updateActivePx(width:number){
    this.width.activePercent.forEach((element:number, i:number)=>{
      this.width.activePx[i] = (element/100* width)*0.45 //this 0.45 is for make padding in circle
      let el = document.getElementById(i.toString())
      if(el)
        this.renderer.setStyle(el, 'font-size', `${this.width.activePx[i]}px`)
    })
  }
  changeCommentState(){
    this.commentStatus.next(!this.commentStatus.value)
  }
  setWidth(n:number):string[]{
    let w:string[] = []
    let list:number[] = n>5?this.width.highCount:this.width.lowCount;
    if(n%2==0)
      list = list.filter((i)=>i!=list[Math.floor(list.length/2)])
    let dif = (list.length-n)/2;
    let scale:number = 0.00
    for(let i=dif; i<list.length - dif; i++){
      scale += list[i]
    }
    for(let i=dif; i<list.length - dif; i++){
      w.push(`width: ${list[i]*100/scale}% !important;`)
    }
    this.width.activePercent = list
    return w
  }
  getIconSizeStyle(index:number):number{
    if(this.data.options.length>5)
      return this.width.highCount[index]
    else
      return this.width.lowCount[index]
  }
  getOptionColorStyle(i:number, id:number=0){
    let centerStyle = 'border-color: gray !important;'
    let leftColor = 'border-color: '+this.data.additional_fields.options_color_on_the_left+' !important;';
    let rightColor = 'border-color: '+this.data.additional_fields.options_color_on_the_right+' !important;';
    let leftBackgroundColor = 'background-color: '+this.data.additional_fields.options_color_on_the_left+' !important;';
    let rightBackgroundColor = 'background-color: '+this.data.additional_fields.options_color_on_the_right+' !important;';
    let centerBackgroundColor = 'background-color: gray !important;';
    if(this.outputData.answer[0]==id){
      leftColor = leftColor+leftBackgroundColor;
      rightColor = rightColor+rightBackgroundColor;
      centerStyle = centerStyle+centerBackgroundColor;
    }
    
    let l = this.data.options.length/2
    let isOdd = this.data.options.length%2==1
    if(isOdd){
      if(i+1<l)
        return rightColor
      else if(i>l)
        return leftColor
      else
        return centerStyle
    }else{
      if(i+1<=l)
        return rightColor
      else
        return leftColor
    }
  }
  getNumber(i:number){
    let l = Math.floor(this.data.options.length/2)
    let isOdd = this.data.options.length%2==1
    if(isOdd)
      return l-i
    else
      return i>=l?l-1-i:l-i
  }
  activeOption(i:number){
    if(this.outputData.answer[0] == i)
      this.outputData.answer = [-1]
    else
      this.outputData.answer = [i];
    if(this.config.isRealTime)
      this.sendOutputData(false)
    if(this.outputData.answer[0]!=-1)
      this.scrollToNextQuestion.emit(this.data.id)
  }
  updateExpandSecData(data:IData){
    this.extraData = data
  }
  sendComment(){
    let form:FormData = new FormData()
    form.append('comment', JSON.stringify(this.extraData.comment))
    this.criteriaService.sendQuestionComment(this.config.quizUserName, this.data.id, form).subscribe(
      ()=>{
        this.commentStatus.next(!this.commentStatus.value)
        this.toastr.success('نظر شما با موفقیت ارسال گردید')
      },
      ()=>{
        this.toastr.error('مشکلی در ارسال نظر شما پیش آمده')
      }
    )
  }
  sendCommentObs(){
    let form:FormData = new FormData()
    form.append('question', this.config.id)
    form.append('comment', JSON.stringify(this.extraData.comment))
    return this.criteriaService.sendMultiCriteriaQuestionComment(form)
  }
  sendOutputData(nextPage:boolean){
    this.outputData.id = this.data.id;
    this.outputData.isUnknown = this.extraData.isUnknown?true:false;
    this.outputData.valid = this.outputData.answer[0] != -1
    if(this.outputData.isUnknown)
      this.outputData.valid = true;
    this.checkFormGroup = nextPage;
    
    this.dataEmitter.emit(this.outputData)
  }
  nextPageAction(){
    this.nextPage$.pipe(
      map(i=>this.sendOutputData(i)),
    ).subscribe()
  }
  getErrorStyle(){
    return {
      'box-shadow': this.checkFormGroup&&this.outputData.answer[0]==-1&&!this.extraData.isUnknown&&this.data.is_answer_required?'0 0 2px 2px rgba(255, 0, 0, 0.507)':'',
      'background-color': this.checkFormGroup&&this.outputData.answer[0]==-1&&!this.extraData.isUnknown&&this.data.is_answer_required?'rgb(255 1 1 / 10%)':'',
    }
  }
  getInitialData(){
    let answerIndex:number = this.answers.findIndex(answer=>answer.id === this.data.id)
    if(answerIndex>-1){
      this.outputData = this.answers[answerIndex]
      this.isUnknownInital = this.answers[answerIndex].isUnknown
    }
  }
  getMaxWidth(i:number):number{
    switch(i){
      case 3:
        return 230;
      case 4:
        return 330;
      case 5:
        return 370;
      case 6:
        return 530;
      case 7:
        return 600;
      case 8:
        return 650;
      case 9:
        return 700;
      default:
        return 1000;
    }
  }
}
